import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { useTranslation } from "react-i18next";

import {
  currentOrgSelector,
  currentUserSelector,
  jwtSelector,
} from "../state/selectors/authSelector";

import type { RootState } from "../state/store";
import { store } from "../state/store";
import {
  bumpCommentsRevision,
  setShouldRerender,
  setSuppressSilentSpinner,
} from "../../features/graph/state/graphuSlice";
import {
  actionDeletedLocally,
  actionUpsertedLocally,
  contentDeletedLocally,
  selectAllGraphContentTypes,
  selectGraphContentByIds,
} from "../../features/graph/state/graphLegacy";
import type { IEventMessage, ITargetObject } from "../util/EventSource";
import type { Action } from "../../features/graph/util/dataTypes/Action";
import type { ContentType } from "../../features/graph/util/dataTypes/ContentType";
import { InitEventSource, getEventSource } from "../util/EventSource";
import axios from "axios";
import apiUrls from "../../api";

import CreatorDashboard from "../../pages/CreatorDashboard/CreatorDashboard";
import Releases from "../../pages/Releases/Releases";
import { EULA } from "../../pages/EULA";
import { Help } from "../../pages/Help";
import { Login, ForgottenPassword, SetPassword } from "../../pages/Login";
import PublicDownloadPage from "../../pages/PublicDownload/PublicDownloadPage";

import Layout from "./Layout";
import ProjectsList from "../../pages/Projects/ProjectsList";
import Templates from "../../pages/Projects/Templates";

import ResetPassword from "../../pages/Login/ResetPassword";
import { Organization, ProfileMain, ProfileWrapper, Subscription } from "../../pages/Profile";

import { logoutAuth } from "../../features/auth/state/authSlice";
import { ProjectGroupsList } from "../../pages/ProjectGroups";
import CreateProjectGroupWrapper from "../../pages/ProjectGroups/CreateProjectGroupWrapper";
import CreateProjectWrapper from "../../pages/Projects/CreateProjectWrapper";
import ProjectGroupEdit from "../../pages/ProjectGroups/ProjectGroupEdit";
import ProjectGroupInformation from "../../pages/ProjectGroups/ProjectGroupInformation";

import FaitTemplateWrapper from "../../features/faitTemplate/components/FaitTemplateWrapper";
import EditProjectGroupNameWrapper from "../../pages/ProjectGroups/EditProjectGroupNameWrapper";
import {
  fetchAnonymousSessionsCount,
  fetchPaEmbedLinks,
  fetchSubscription,
  getSubscriptionStatus,
  setSubscriptionState,
} from "../../features/subscription/state/subscriptionSlice";
import NoSubscriptionHome from "../../pages/NoSubscriptionHome/NoSubscriptionHome";
import ErrorView from "../components/ErrorBoundary/ErrorView";
import ErrorBoundary from "../components/ErrorBoundary/ErrorBoundary";
import { clearAlerts, setAlert } from "../../features/alert/state/alertsSlice";
import { Gallery } from "../../features/gallery/Gallery";
import ZendeskChat from "../components/ZendeskChat/ZendeskChat";
import { MobileDashboard } from "../../mobilePages/MobileDashboard";
import { MobileUploadSelectedFile } from "../../mobilePages/MobileUploadSelectedFile";
import { MobileUploadsList } from "../../mobilePages/MobileUploadsList";
import MobileLayout from "./MobileLayout";
import EnduserProjectGroupsList from "../../enduserPages/EnduserProjectGroupsList/EnduserProjectGroupsList";
import EnduserProjectsList from "../../enduserPages/EnduserProjectsList/EnduserProjectsList";
import { isStringNullOrEmpty } from "../util/isStringNullOrEmpty";
import ProjectGroupCollaborators from "../../pages/ProjectGroups/ProjectGroupCollaborators";
import ProjectGroupLearnersWrapper from "../../pages/ProjectGroups/ProjectGroupLearnersWrapper";
import ProjectGroupLearnersList from "../../pages/ProjectGroups/ProjectGroupLearnersList";
import ProjectGroupLearnersInvite from "../../pages/ProjectGroups/ProjectGroupLearnersInvite";
import ProjectGroupLearnersUpdate from "../../pages/ProjectGroups/ProjectGroupLearnersUpdate";
import { getFromLocalStorage, storeInLocalStorage } from "../util/localStorage";
import detectIfMobileBrowser from "../util/DetectIfMobileBrowser";
import { GlobalModals } from "../components/GlobalModals/GlobalModals";
import { productionMode } from "./App";
import { fetchBuilds, fetchCurrentProjectGroupBuilds } from "src/features/builds/state/buildsSlice";
import { getSelectedProjectGroup } from "src/features/projectGroups/state/projectGroupsSlice";

import {
  CollaboratorsList,
  CollaboratorsWrapper,
  CreateCollaborator,
  UpdateCollaborator,
} from "src/pages/Collaborators";
import {
  CreateParticipant,
  NewParticipants,
  ParticipantsCsvImport,
  ParticipantsList,
  ParticipantsWrapper,
  UpdateParticipant,
} from "src/pages/Participants";
import {
  getMedia,
  setBlockadeLabsImagePreview,
  setUploadHasBeenTranscribed,
} from "src/features/gallery/state/gallerySlice";
import type { IBlockadeLabsPreview } from "src/features/gallery/state/gallerySlice";
import { getAllSessionsByProjectGroup } from "src/features/stats/state/statsSlice";
import { STATS_OVERVIEW_PROJECT_GROUPS_SESSIONS_COMPONENT_ID } from "src/features/stats/components/StatsOverview/StatsOverview";
import {
  getAllProjects,
  getCurrentProject,
  setCurrentProjectFromId,
} from "src/features/projects/state/projectsSlice";
import { ANONYMOUS_SESSION_COUNTER_COMPONENT } from "src/features/subscription/components/AnonymousSessionsCounter";
import Spinner from "../components/Loader/Spinner";

const Main = lazy(() => import("../../pages/Projects/MainProject"));
const ProjectEdit = lazy(() => import("../../pages/Projects/ProjectEdit"));
const ProjectGraph = lazy(() =>
  import("../../pages/Projects/ProjectGraph").then((m) => ({ default: m.ProjectGraph })),
);
const NodeScreen = lazy(() =>
  import("../../pages/Projects/ProjectGraph").then((m) => ({ default: m.NodeScreen })),
);
const CreateNewScene = lazy(() =>
  import("../../features/graph/components/CreateNewScene").then((m) => ({
    default: m.CreateNewScene,
  })),
);
const BackgroundMusic = lazy(() =>
  import("../../features/graph/components/BackgroundMusic").then((m) => ({
    default: m.BackgroundMusic,
  })),
);
const ProjectMedias = lazy(() => import("../../pages/Projects/ProjectMedias"));
const ProjectInfo = lazy(() => import("../../pages/Projects/ProjectInformation"));
const ProjectParticipants = lazy(() => import("../../pages/Projects/ProjectParticipants"));
const EditProjectNameWrapper = lazy(() => import("../../pages/Projects/EditProjectNameWrapper"));
const EditProjectLanguageWrapper = lazy(
  () => import("../../pages/Projects/EditProjectLanguageWrapper"),
);
const EditProjectThumbnailWrapper = lazy(
  () => import("../../pages/Projects/EditProjectThumbnailWrapper"),
);

const ProjectGroupPublish = lazy(() => import("../../pages/ProjectGroups/ProjectGroupPublish"));
const AskPublicationWrapper = lazy(
  () => import("../../features/projectGroupPublication/components/AskPublicationWrapper"),
);
const PublishNewVersion = lazy(
  () => import("../../features/projectGroupPublication/components/PublishNewVersion"),
);
const PublishSameVersionOnNewPlatformsWrapper = lazy(
  () =>
    import(
      "../../features/projectGroupPublication/components/PublishSameVersionOnNewPlatformsWrapper"
    ),
);
const ProjectGroupMedias = lazy(() => import("../../pages/ProjectGroups/ProjectGroupMedias"));
const EditProjectGroupThemeWrapper = lazy(
  () => import("../../pages/ProjectGroups/EditProjectGroupThemeWrapper"),
);
const EditProjectGroupThumbnailWrapper = lazy(
  () => import("../../pages/ProjectGroups/EditProjectGroupThumbnailWrapper"),
);
const EditProjectGroupImageWrapper = lazy(
  () => import("../../pages/ProjectGroups/EditProjectGroupImageWrapper"),
);
const DuplicateProjectWrapper = lazy(
  () => import("src/features/duplicateProject/components/DuplicateProjectWrapper"),
);
const DuplicateProjectGroupWrapper = lazy(
  () => import("src/features/duplicateProjectGroup/components/DuplicateProjectGroupWrapper"),
);

const StatisticsLayout = lazy(() => import("../../pages/StatisticsNew/StatisticsLayout"));
const GlobalView = lazy(() => import("../../pages/StatisticsNew/GlobalView"));
const ParcoursListView = lazy(() => import("../../pages/StatisticsNew/ParcoursListView"));
const ParcoursDetailView = lazy(() => import("../../pages/StatisticsNew/ParcoursDetailView"));
const ModuleView = lazy(() => import("../../pages/StatisticsNew/ModuleView"));
const ApprenantsListView = lazy(() => import("../../pages/StatisticsNew/ApprenantsListView"));
const ApprenantDetailView = lazy(() => import("../../pages/StatisticsNew/ApprenantDetailView"));
const ClassementView = lazy(() => import("../../pages/StatisticsNew/ClassementView"));
const ComparateurView = lazy(() => import("../../pages/StatisticsNew/ComparateurView"));
const ReportsListView = lazy(() => import("../../pages/StatisticsNew/ReportsListView"));
const ReportsFormView = lazy(() => import("../../pages/StatisticsNew/ReportsFormView"));

const EnduserStatsLayout = lazy(() => import("../../pages/EnduserStats/EnduserStatsLayout"));
const EnduserStatsOverviewPage = lazy(
  () => import("../../pages/EnduserStats/EnduserStatsOverviewPage"),
);
const EnduserParcoursListView = lazy(
  () => import("../../pages/EnduserStats/EnduserParcoursListView"),
);
const EnduserParcoursDetailView = lazy(
  () => import("../../pages/EnduserStats/EnduserParcoursDetailView"),
);
const EnduserModuleDetailView = lazy(
  () => import("../../pages/EnduserStats/EnduserModuleDetailView"),
);
const EnduserSessionDetailView = lazy(
  () => import("../../pages/EnduserStats/EnduserSessionDetailView"),
);
const EnduserGazeHeatmapView = lazy(
  () => import("../../pages/EnduserStats/EnduserGazeHeatmapView"),
);
const EnduserComparateurView = lazy(
  () => import("../../pages/EnduserStats/EnduserComparateurView"),
);

const DownloadClient = lazy(() => import("../../pages/DownloadClient/DownloadClient"));
const DeviceManager = lazy(() => import("../../pages/DeviceManager/DeviceManager"));

const TemplatesWrapper = lazy(() => import("src/pages/Templates/TemplatesWrapper"));
const TemplatesList = lazy(() => import("src/pages/Templates/TemplatesList"));
const SpecificTemplateWrapper = lazy(() => import("src/pages/Templates/SpecificTemplateWrapper"));
const ThemesWrapper = lazy(() => import("src/pages/Themes/ThemesWrapper"));
const ThemesList = lazy(() => import("src/pages/Themes/ThemesList"));
const HelpCenter = lazy(() => import("src/pages/Help/HelpCenter"));

const ChatWrapper = lazy(() => import("src/pages/Chat/ChatWrapper"));
const ScenarioHelperChat = lazy(() => import("src/pages/Chat/ScenarioHelperChat"));
const WixarpediaChat = lazy(() => import("src/pages/Chat/WixarpediaChat"));

const AgentsWrapper = lazy(() => import("src/pages/Agents/AgentsWrapper"));
const AgentList = lazy(() => import("src/pages/Agents/AgentList"));
const AgentDetail = lazy(() => import("src/pages/Agents/AgentDetail"));
const AgentForm = lazy(() => import("src/pages/Agents/AgentForm"));
const DocumentsList = lazy(() => import("src/pages/Agents/DocumentsList"));

const PreviewPage = lazy(() => import("src/pages/Preview/PreviewPage"));

const PROJECT_GROUP_PUBLISH_COMPONENT_ID = "projectGroupPublishTableId";

const HEARTBEAT_TIMEOUT_MS = 30000;

const toBlockadeLabsPreview = (target: ITargetObject): IBlockadeLabsPreview => ({
  thumbnailUrl: target.thumbnailUrl ?? "",
  imageUrl: target.imageUrl ?? "",
  faultyImageId: target.faultyImageId,
  blockadeLabImageId:
    target.blockadeLabImageId !== undefined ? Number(target.blockadeLabImageId) : undefined,
  errorMessage: target.errorMessage,
  id: target.id,
});

const Router = () => {
  const jwt = useAppSelector(jwtSelector);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const subscriptionStatus = useAppSelector(getSubscriptionStatus);
  const isSubscriptionRunning = subscriptionStatus === "Active";

  const [screenSize, setScreenSize] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  const lastHeartbeatRef = useRef(Date.now());
  const graphReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isMobile = detectIfMobileBrowser();

  const hasHandledRefreshRedirect = useRef(false);

  const hasOpenedSseRef = useRef(false);

  const currentProjectGroup = useAppSelector(getSelectedProjectGroup);
  const currentProjectGroupIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    currentProjectGroupIdRef.current = currentProjectGroup?.id;
  }, [currentProjectGroup?.id]);

  useEffect(() => {
    dispatch(clearAlerts());
    if (jwt) {
      const unauthRoutes = ["/login", "/forgottenpassword", "/reset-password"];
      if (unauthRoutes.includes(window.location.pathname)) {
        navigate("/");
      }
    }
  }, []);

  const { user, organization } = useAppSelector((state: RootState) => {
    return {
      user: currentUserSelector(state),
      organization: currentOrgSelector(state),
    };
  });

  const checkNewCommit = (lastCom: string) => {
    const currentCommitHash = getFromLocalStorage("CURRENT_COMMIT_HASH");
    if (lastCom !== currentCommitHash && !isStringNullOrEmpty(lastCom)) {
      storeInLocalStorage("CURRENT_COMMIT_HASH", lastCom);
      window.location.href = `${window.location.origin}/?nocache=${new Date().getTime()}`;
    }
  };

  useEffect(() => {
    if (!jwt || !user?.id) return undefined;

    const handleSseOpen = () => {
      lastHeartbeatRef.current = Date.now();
      if (hasOpenedSseRef.current) {
        runSseCatchUp();
      } else {
        hasOpenedSseRef.current = true;
      }
    };
    const handleSseError = (e: Event) => console.error("SSE error: ", e);

    const es = getEventSource();
    if (es && es.readyState !== EventSource.CLOSED) {
      es.onopen = handleSseOpen;
      es.onmessage = handleSSEMessage;
      es.onerror = handleSseError;
    } else {
      InitEventSource(jwt, {
        onopen: handleSseOpen,
        onerror: handleSseError,
        onmessage: handleSSEMessage,
      });
    }

    const watchdog = setInterval(() => {
      if (!jwt) return;
      if (document.visibilityState !== "visible") {
        lastHeartbeatRef.current = Date.now();
        return;
      }
      if (Date.now() - lastHeartbeatRef.current > HEARTBEAT_TIMEOUT_MS) {
        lastHeartbeatRef.current = Date.now();
        InitEventSource(jwt, {
          onopen: handleSseOpen,
          onerror: handleSseError,
          onmessage: handleSSEMessage,
        });
      }
    }, 10000);

    return () => {
      clearInterval(watchdog);
      const current = getEventSource();
      if (current) current.close();
    };
  }, [jwt, user?.id]);

  function runSseCatchUp() {
    void dispatch(fetchBuilds({ componentId: PROJECT_GROUP_PUBLISH_COMPONENT_ID }));
    const projectGroupId = currentProjectGroupIdRef.current;
    if (projectGroupId) {
      void dispatch(
        fetchCurrentProjectGroupBuilds({
          projectGroupId,
          componentId: PROJECT_GROUP_PUBLISH_COMPONENT_ID,
        }),
      );
    }
    void dispatch(fetchSubscription({}));
  }

  function handleSSEMessage(event: MessageEvent<string>) {
    const eventMessage = JSON.parse(event.data) as IEventMessage;
    const currentUserId = Number(user?.id);
    const selfEnvId = isMobile ? 4 : 2;
    const isSelfEcho =
      Number(eventMessage.issuedFromUser) === currentUserId &&
      Number(eventMessage.issuedFromEnv) === selfEnvId;

    const disconnect = () => {
      alert(`${t("disconnected")}`);
      dispatch(logoutAuth());
    };

    if (!productionMode && eventMessage.action !== "001_HEARTBEAT") {
      console.warn("SSE received: ", eventMessage);
    }

    switch (eventMessage.action) {
      case "101_DISCONNECT_ONE": {
        const environmentId = eventMessage.issuedFromEnv;

        if (Number(eventMessage?.issuedFromUser) === currentUserId) {
          if (Number(environmentId) === 2 && !isMobile) {
            disconnect();
          }
        }
        break;
      }

      case "401_SUBSCRIPTION_EXPIRED": {
        dispatch(setSubscriptionState("Terminated"));
        break;
      }

      case "207_NEW_SOURCE_MEDIA_CACHE":
      case "501_MEDIA_TRANSCRIPTION_FAILED": {
        const mediaId = eventMessage.targetObjects.find((to) => to.table === "source-media")
          ?.id as number;
        const emitterOrganizationId = eventMessage.targetObjects.find(
          (to) => to.table === "organization",
        )?.id;
        if (Number(emitterOrganizationId) === Number(organization?.id)) {
          void dispatch(getMedia({ mediaId }));
          dispatch(setUploadHasBeenTranscribed(mediaId));
          if (eventMessage.action === "501_MEDIA_TRANSCRIPTION_FAILED") {
            dispatch(
              setAlert({
                type: "warning",
                msg: t("alerts.transcriptionFailed"),
                subMsg: t("alerts.transcriptionFailedSubtext"),
              }),
            );
          }
        }
        break;
      }

      case "305_UPDATED_PROJECT": {
        void dispatch(getAllProjects({ force: true, componentId: "projectListComponent" })).then(
          () => {
            dispatch(setCurrentProjectFromId(Number(eventMessage.targetObjects[0].id)));
          },
        );
        break;
      }

      case "001_HEARTBEAT": {
        lastHeartbeatRef.current = Date.now();
        break;
      }

      case "201_NEW_WEB_RELEASE": {
        checkNewCommit(eventMessage.targetObjects[0]?.commit_hash ?? "");
        break;
      }

      case "304_UPDATED_BUILD_STATUS": {
        void dispatch(fetchBuilds({ componentId: PROJECT_GROUP_PUBLISH_COMPONENT_ID }));
        const projectGroupId = currentProjectGroupIdRef.current;
        if (projectGroupId) {
          void dispatch(
            fetchCurrentProjectGroupBuilds({
              projectGroupId,
              componentId: PROJECT_GROUP_PUBLISH_COMPONENT_ID,
            }),
          );
        }
        void dispatch(
          getAllSessionsByProjectGroup({
            componentId: STATS_OVERVIEW_PROJECT_GROUPS_SESSIONS_COMPONENT_ID,
          }),
        );
        break;
      }

      case "331_BLOCKADE_LAB_GENERATION_COMPLETED":
      case "332_BLOCKADE_LAB_GENERATION_ERROR": {
        const data = eventMessage.targetObjects?.find((o) => o.blockadeLabImageId);
        if (data) dispatch(setBlockadeLabsImagePreview(toBlockadeLabsPreview(data)));
        break;
      }

      case "307_UPDATE_PROABONO_SUBSCRIPTION": {
        const data = eventMessage.targetObjects[0];
        const orgId = data?.id;
        if (Number(orgId) === Number(organization?.id)) {
          void dispatch(fetchPaEmbedLinks({}));
          void dispatch(
            fetchAnonymousSessionsCount({ componentId: ANONYMOUS_SESSION_COUNTER_COMPONENT }),
          );
        }
        break;
      }

      case "309_UPDATED_CONTENT_LINKS": {
        if (isSelfEcho) break;
        const graphContents = selectGraphContentByIds(store.getState());
        const touchesOpenGraph = eventMessage.targetObjects.some(
          (to) =>
            (to.table === "content_scene" || to.table === "content_new_button") &&
            Boolean(graphContents[Number(to.id)]),
        );
        if (!touchesOpenGraph) break;
        const idsOf = (table: string) =>
          eventMessage.targetObjects
            .filter((to) => to.table === table)
            .map((to) => Number(to.id))
            .filter((n) => Number.isFinite(n) && n > 0);
        const structural =
          idsOf("content_new_button").length > 0 || idsOf("content_reparented").length > 0;
        const deletedIds = idsOf("action_deleted");
        const upsertIds = [...idsOf("action_created"), ...idsOf("action_updated")];
        const fullSilentReload = () => {
          if (graphReloadTimerRef.current) clearTimeout(graphReloadTimerRef.current);
          graphReloadTimerRef.current = setTimeout(() => {
            dispatch(setShouldRerender());
          }, 300);
        };
        if (structural || (deletedIds.length === 0 && upsertIds.length === 0)) {
          fullSilentReload();
          break;
        }
        deletedIds.forEach((id) => dispatch(actionDeletedLocally(id)));
        if (upsertIds.length > 0) {
          axios
            .post<{ ok: boolean; actions: Action[] }>(apiUrls.projects.actionsByIds, {
              actionIds: upsertIds,
            })
            .then((res) => {
              const rows = res.data.actions;
              rows.forEach((row) => dispatch(actionUpsertedLocally(row)));
              const returned = new Set(rows.map((r) => Number(r.id)));
              upsertIds
                .filter((id) => !returned.has(id))
                .forEach((id) => dispatch(actionDeletedLocally(id)));
              dispatch(setSuppressSilentSpinner(true));
            })
            .catch(() => fullSilentReload());
        } else {
          dispatch(setSuppressSilentSpinner(true));
        }
        break;
      }

      case "310_UPDATED_GRAPH_COMMENTS": {
        if (isSelfEcho) break;
        const openProjectId = Number(getCurrentProject(store.getState())?.id);
        const touchesOpenProject = eventMessage.targetObjects.some(
          (to) => to.table === "project" && Number(to.id) === openProjectId,
        );
        if (!touchesOpenProject) break;
        dispatch(bumpCommentsRevision());
        break;
      }

      case "208_NEW_INTERACTION": {
        if (isSelfEcho) break;
        const graphContents = selectGraphContentByIds(store.getState());
        const parentInOpenGraph = eventMessage.targetObjects.some(
          (to) => to.table === "content_parent_scene" && Boolean(graphContents[Number(to.id)]),
        );
        if (!parentInOpenGraph) break;
        if (graphReloadTimerRef.current) clearTimeout(graphReloadTimerRef.current);
        graphReloadTimerRef.current = setTimeout(() => {
          dispatch(setShouldRerender());
        }, 300);
        break;
      }

      case "302_UPDATED_CONTENT_TYPE": {
        if (isSelfEcho) break;
        const graphContents = selectGraphContentByIds(store.getState());
        const touchedContentIds = eventMessage.targetObjects
          .filter((to) => to.table === "content" && Boolean(graphContents[Number(to.id)]))
          .map((to) => Number(to.id));
        if (touchedContentIds.length === 0) break;
        const editMatch = window.location.pathname.match(/\/graph\/node\/([^/]+)\/(\d+)/);
        if (editMatch) {
          const [, editedModelName, editedCtId] = editMatch;
          const editedCt = selectAllGraphContentTypes(store.getState()).find(
            (ct: ContentType) =>
              ct.modelName === editedModelName && Number(ct.object?.id) === Number(editedCtId),
          );
          if (editedCt && touchedContentIds.includes(Number(editedCt.object.content))) {
            dispatch(
              setAlert({
                isOpen: true,
                msg: t("alerts.nodeEditedElsewhere"),
                type: "warning",
                customTimeoutInMs: 12000,
              }),
            );
          }
        }
        if (graphReloadTimerRef.current) clearTimeout(graphReloadTimerRef.current);
        graphReloadTimerRef.current = setTimeout(() => {
          dispatch(setShouldRerender());
        }, 300);
        break;
      }

      case "404_DELETE_CONTENT_TYPE": {
        if (isSelfEcho) break;
        const graphContents = selectGraphContentByIds(store.getState());
        const deletedContentIds = eventMessage.targetObjects
          .filter((to) => to.table === "content" && Boolean(graphContents[Number(to.id)]))
          .map((to) => Number(to.id));
        if (deletedContentIds.length === 0) break;
        deletedContentIds.forEach((id) => dispatch(contentDeletedLocally(id)));
        dispatch(setSuppressSilentSpinner(true));
        break;
      }

      case "405_DELETE_CONTENT_SCENE": {
        if (isSelfEcho) break;
        const graphContents = selectGraphContentByIds(store.getState());
        const sceneInOpenGraph = eventMessage.targetObjects.some(
          (to) => to.table === "content" && Boolean(graphContents[Number(to.id)]),
        );
        if (!sceneInOpenGraph) break;
        if (graphReloadTimerRef.current) clearTimeout(graphReloadTimerRef.current);
        graphReloadTimerRef.current = setTimeout(() => {
          dispatch(setShouldRerender());
        }, 300);
        break;
      }

      default:
        break;
    }
  }

  useEffect(() => {
    if (jwt) {
      void dispatch(fetchSubscription({}));
    }
  }, [jwt]);

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [screenSize.height]);

  const lstorage = localStorage.getItem("wixar.auth.jwt");
  useEffect(() => {
    if (isStringNullOrEmpty(lstorage)) {
      dispatch(logoutAuth());
    }
  }, [lstorage]);

  const renderTheAppropriateRouter = () => {
    if (window.location.pathname.startsWith("/download/")) {
      return (
        <Routes>
          <Route path="/download/:token" element={<PublicDownloadPage />} />
        </Routes>
      );
    }

    /* We have several cases to handle:
    - User is not subscribed
    - User is subscribed & hasn't accepted EULA
    - User is subscribed, has accepted EULA and is on Mobile device
    - User is subscribed, has accepted EULA and is on Mobile device
    */

    if (!hasHandledRefreshRedirect.current) {
      hasHandledRefreshRedirect.current = true;
      const currentPath = window.location.pathname;
      if (/\/\d+/.test(currentPath) && !currentPath.startsWith("/download/")) {
        return (
          <Routes>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        );
      }
    }

    if (jwt && !user) {
      return null;
    }

    if (user && !isSubscriptionRunning) {
      return (
        <>
          <Routes>
            <Route index element={<NoSubscriptionHome />} />
            <Route path="*" element={<Navigate to={""} />} />
          </Routes>
        </>
      );
    }

    if (user && !user?.has_accepted_cgv) {
      return (
        <Routes>
          <Route index element={<EULA />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgottenpassword" element={<ForgottenPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password" element={<SetPassword />} />
        </Routes>
      );
    }

    if (user?.role.name === "Enduser") {
      return (
        <ErrorBoundary currentUserId={Number(user?.id)}>
          <Routes>
            <Route path="/login" element={<Navigate to="/stats" replace />} />
            <Route path="/forgottenpassword" element={<Navigate to="/stats" replace />} />
            <Route path="/reset-password" element={<Navigate to="/stats" replace />} />
            <Route path="/set-password" element={<Navigate to="/stats" replace />} />

            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/stats" replace />} />

              <Route path="profile" element={<ProfileWrapper enduser />}>
                <Route index element={<ProfileMain />} />
                <Route path="me" element={<ProfileMain />} />
                <Route path="organization" element={<Organization />} />
              </Route>

              <Route path="courses" element={<EnduserProjectGroupsList />} />
              <Route path="courses/:id" element={<EnduserProjectsList />}>
                <Route index element={<EnduserProjectsList />} />
                <Route path="list" element={<EnduserProjectsList />} />
                <Route path="todo" element={<EnduserProjectsList />} />
                <Route path="pending" element={<EnduserProjectsList />} />
                <Route path="completed" element={<EnduserProjectsList />} />
              </Route>

              <Route path="stats" element={<EnduserStatsLayout />}>
                <Route index element={<EnduserStatsOverviewPage />} />
                <Route path="parcours" element={<EnduserParcoursListView />} />
                <Route path="parcours/:parcoursId" element={<EnduserParcoursDetailView />} />
                <Route
                  path="parcours/:parcoursId/:moduleId"
                  element={<EnduserModuleDetailView />}
                />
                <Route
                  path="parcours/:parcoursId/:moduleId/session/:sessionId"
                  element={<EnduserSessionDetailView />}
                />
                <Route
                  path="parcours/:parcoursId/:moduleId/session/:sessionId/heatmap"
                  element={<EnduserGazeHeatmapView />}
                />
                <Route path="comparateur" element={<EnduserComparateurView />} />
              </Route>

              <Route path="download-client" element={<DownloadClient />} />
              <Route path="installations" element={<DeviceManager />} />
              <Route path="/error" element={<ErrorView />} />
              <Route
                path="*"
                element={
                  <main style={{ padding: "1rem" }}>
                    <p>Theres nothing here!</p>
                  </main>
                }
              />
            </Route>
          </Routes>
        </ErrorBoundary>
      );
    } else if (
      /* Case: user is subscribed & on Mobile screen, portrait mode */
      isMobile
      //&& !isInLandscapeMode
    ) {
      return (
        <>
          <ErrorBoundary currentUserId={Number(user?.id)}>
            <Routes>
              <Route path="/login" element={<Login mobile={isMobile} />} />
              <Route path="/forgottenpassword" element={<ForgottenPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/set-password" element={<SetPassword />} />

              <Route path="/" element={<MobileLayout screenSize={screenSize} />}>
                <Route index element={<MobileDashboard />} />
                <Route path="/upload" element={<MobileUploadSelectedFile />} />
                <Route path="/uploads" element={<MobileUploadsList />} />
              </Route>
            </Routes>
          </ErrorBoundary>
          <ZendeskChat />
        </>
      );
    } else {
      /* Case: user is subscribed & on Desktop screen */
      return (
        <>
          <ErrorBoundary currentUserId={Number(user?.id)}>
            <Routes location={location} key={location.pathname}>
              <Route path="/login" element={<Login />} />
              <Route path="/forgottenpassword" element={<ForgottenPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/set-password" element={<SetPassword />} />

              <Route
                path="/preview"
                element={
                  <Suspense fallback={<Spinner />}>
                    <PreviewPage />
                  </Suspense>
                }
              />

              <Route path="/" element={<Layout />}>
                <Route index element={<CreatorDashboard />} />

                <Route path="courses" element={<ProjectGroupsList />} />
                <Route path="courses/:id" element={<ProjectGroupEdit />}>
                  <Route path="list" element={<ProjectsList />} />
                  <Route path="preview" element={null} />
                  <Route path="publish" element={<ProjectGroupPublish />} />
                  <Route path="information" element={<ProjectGroupInformation />} />
                  <Route path="medias" element={<ProjectGroupMedias />} />
                  <Route path="publish/new" element={<AskPublicationWrapper />}>
                    <Route path="version" element={<PublishNewVersion />} />
                    <Route path="platforms" element={<PublishSameVersionOnNewPlatformsWrapper />} />
                  </Route>
                  <Route path="team" element={<ProjectGroupCollaborators />} />
                  <Route path="learners" element={<ProjectGroupLearnersWrapper />}>
                    <Route index element={<ProjectGroupLearnersList />} />
                    <Route path="new" element={<ProjectGroupLearnersInvite />} />
                    <Route path=":learnerId" element={<ProjectGroupLearnersUpdate />} />
                  </Route>
                </Route>

                <Route path="courses/new" element={<CreateProjectGroupWrapper />} />
                <Route path="courses/:id/edit" element={<EditProjectGroupNameWrapper />} />
                <Route path="courses/:id/edit-name" element={<EditProjectGroupNameWrapper />} />
                <Route
                  path="courses/:id/edit-thumbnail"
                  element={<EditProjectGroupThumbnailWrapper />}
                />
                <Route path="courses/:id/edit-image" element={<EditProjectGroupImageWrapper />} />
                <Route path="courses/:id/edit-theme" element={<EditProjectGroupThemeWrapper />} />
                <Route path="courses/:id/new" element={<Templates />} />
                <Route path="courses/:id/new/:templateId" element={<CreateProjectWrapper />} />
                <Route
                  path="courses/:id/duplicate/:projectId"
                  element={<DuplicateProjectWrapper />}
                />
                <Route
                  path="courses/:id/duplicateGroup"
                  element={<DuplicateProjectGroupWrapper />}
                />
                <Route path="courses/:id/list/:projectId" element={<Main />}>
                  <Route index element={<ProjectEdit />} />
                  <Route path="graph" element={<ProjectGraph />} />
                  <Route path="preview" element={null} />
                  <Route path="medias" element={<ProjectMedias />} />
                  <Route path="information" element={<ProjectInfo />} />
                  <Route
                    path="graph/background-music/edit"
                    element={<BackgroundMusic mode="edit" />}
                  />
                  <Route path="graph/node/:contentType/:nodeId" element={<NodeScreen />} />
                  <Route path="graph/create/scene" element={<CreateNewScene />} />
                  <Route path="learners" element={<ProjectParticipants />} />
                </Route>
                <Route
                  path="courses/:id/list/:projectId/graph/edit-name"
                  element={<EditProjectNameWrapper />}
                />
                <Route
                  path="courses/:id/list/:projectId/graph/edit-language"
                  element={<EditProjectLanguageWrapper mode="edit-language" />}
                />
                <Route
                  path="courses/:id/list/:projectId/graph/edit-thumbnail"
                  element={<EditProjectThumbnailWrapper />}
                />
                <Route
                  path="courses/:id/list/:projectId/graph/duplicate"
                  element={<DuplicateProjectWrapper />}
                />
                <Route
                  path="courses/:id/list/:projectId/graph/template"
                  element={<FaitTemplateWrapper />}
                />
                <Route
                  path="courses/:id/list/:projectId/edit"
                  element={<EditProjectNameWrapper />}
                />
                <Route
                  path="courses/:id/list/:projectId/edit-name"
                  element={<EditProjectNameWrapper />}
                />
                <Route
                  path="courses/:id/list/:projectId/edit-language"
                  element={<EditProjectLanguageWrapper mode="edit-language" />}
                />
                <Route
                  path="courses/:id/list/:projectId/edit-thumbnail"
                  element={<EditProjectThumbnailWrapper />}
                />
                <Route path="courses/:id/template/:projectId" element={<FaitTemplateWrapper />} />

                <Route path="statistics" element={<StatisticsLayout />}>
                  <Route index element={<GlobalView />} />
                  <Route path="parcours" element={<ParcoursListView />} />
                  <Route path="parcours/:parcoursId" element={<ParcoursDetailView />} />
                  <Route path="parcours/:parcoursId/:moduleId" element={<ModuleView />} />
                  <Route path="apprenants" element={<ApprenantsListView />} />
                  <Route path="apprenants/:userId" element={<ApprenantDetailView />} />
                  <Route path="apprenants/:userId/:parcoursId" element={<ParcoursDetailView />} />
                  <Route path="apprenants/:userId/:parcoursId/:moduleId" element={<ModuleView />} />
                  <Route path="classement" element={<ClassementView />} />
                  <Route path="comparateur" element={<ComparateurView />} />
                  <Route path="reports" element={<ReportsListView />} />
                  <Route path="reports/new" element={<ReportsFormView mode="create" />} />
                  <Route path="reports/:id/edit" element={<ReportsFormView mode="edit" />} />
                  <Route path="group" element={<Navigate to="/statistics" replace />} />
                  <Route
                    path="learners"
                    element={<Navigate to="/statistics/apprenants" replace />}
                  />
                </Route>
                <Route path="profile" element={<ProfileWrapper />}>
                  <Route index element={<ProfileMain />} />
                  <Route path="me" element={<ProfileMain />} />
                  <Route path="organization" element={<Organization />} />
                  <Route path="subscription" element={<Subscription />} />
                </Route>

                <Route path="download-client" element={<DownloadClient />} />
                <Route path="installations" element={<DeviceManager />} />
                {process.env.REACT_APP_HIDE_RELEASES !== "true" && (
                  <Route path="releases" element={<Releases />} />
                )}
                <Route path="help" element={<Help />} />
                <Route path="help-center" element={<HelpCenter />} />
                <Route path="medias" element={<Gallery />} />
                <Route path="chat" element={<ChatWrapper />}>
                  <Route path="scenario-helper" element={<ScenarioHelperChat />} />
                  <Route path="wixarpedia" element={<WixarpediaChat />} />
                </Route>

                <Route path="agents" element={<AgentsWrapper />}>
                  <Route index element={<Navigate to="list" replace />} />
                  <Route path="list" element={<AgentList />} />
                  <Route path="documents" element={<DocumentsList />} />
                  <Route path="new" element={<AgentForm />} />
                  <Route path=":id" element={<AgentDetail />} />
                  <Route path=":id/settings" element={<AgentForm />} />
                </Route>

                <Route path="team" element={<CollaboratorsWrapper />}>
                  <Route index element={<CollaboratorsList />} />
                  <Route path="list" element={<CollaboratorsList />} />
                  <Route path=":id" element={<UpdateCollaborator />} />
                  <Route path="new" element={<CreateCollaborator />} />
                  {}
                </Route>
                <Route path="collaborators" element={<CollaboratorsWrapper />}>
                  <Route index element={<CollaboratorsList />} />
                  <Route path="list" element={<CollaboratorsList />} />
                  <Route path=":id" element={<UpdateCollaborator />} />
                  <Route path="new" element={<CreateCollaborator />} />
                </Route>
                <Route path="learners" element={<ParticipantsWrapper />}>
                  <Route index element={<ParticipantsList />} />
                  <Route path="list" element={<ParticipantsList />} />
                  <Route path=":id" element={<UpdateParticipant />} />
                  <Route path="new" element={<NewParticipants />} />
                  <Route path="new/invite-authenticated" element={<CreateParticipant />} />
                  <Route
                    path="new/invite-simplified"
                    element={<CreateParticipant authType="simplified" />}
                  />
                  <Route path="new/invite" element={<NewParticipants />} />
                  <Route path="new/csv-import" element={<ParticipantsCsvImport />} />
                </Route>
                <Route path="templates" element={<TemplatesWrapper />}>
                  <Route index element={<TemplatesList />} />
                </Route>
                <Route path="templates/:id" element={<SpecificTemplateWrapper />}>
                  <Route index element={<ProjectGraph />} />
                  <Route path="node/:contentType/:nodeId" element={<NodeScreen />} />
                  <Route path="create/scene" element={<CreateNewScene />} />
                  <Route path="background-music/edit" element={<BackgroundMusic mode="edit" />} />
                  <Route path="background-music/view" element={<BackgroundMusic mode="view" />} />
                </Route>
                <Route path="templates/:id/new" element={<FaitTemplateWrapper />} />
                <Route path="themes" element={<ThemesWrapper />}>
                  <Route index element={<ThemesList />} />
                </Route>

                <Route
                  path="*"
                  element={
                    <main style={{ padding: "1rem" }}>
                      <p>Theres nothing here!</p>
                    </main>
                  }
                />
              </Route>

              <Route path="/error" element={<ErrorView />} />
            </Routes>
          </ErrorBoundary>
          <GlobalModals />
          <ZendeskChat />
        </>
      );
    }
  };

  return renderTheAppropriateRouter();
};

export default Router;
